import type { Post } from '../blogTypes'

export const planningPermission: Post = {
  slug: 'dormer-planning-permission-guide',
  title: 'Dormer Planning Permission Guide: When You Need It and How to Apply',
  metaTitle: 'Dormer Planning Permission Guide: Rules, Limits and How to Apply',
  description:
    'Do you need planning permission for a dormer? Learn about permitted development limits, building permits, conservation areas and how to avoid costly mistakes.',
  excerpt:
    'Many dormers can be built without a full planning application, but there are strict limits. Check the rules before you design anything.',
  category: 'cost-planning',
  image: 'https://images.unsplash.com/photo-1762423779989-025785197ba9?auto=format&fit=crop&w=1200&q=75',
  imageAlt: 'Row of houses with dormer windows under a cloudy sky',
  datePublished: '2025-01-28',
  dateModified: '2025-03-10',
  readMinutes: 8,
  blocks: [
    {
      type: 'p',
      text: 'Planning rules decide how big your dormer can be, where it can sit and how it must look. Rules differ by country, state, county and even street, so this guide explains the common patterns and the questions to ask your local authority. It is general information, not legal advice.',
    },
    { type: 'h2', text: 'Do you need planning permission for a dormer?' },
    {
      type: 'p',
      text: 'In England, many rear dormers fall under **permitted development**, which means no full planning application is needed if you stay inside set limits. In the United States there is no national rule: you will almost always need a **building permit**, and some towns also require zoning or design review. Always check with your local building department before you commit to a design.',
    },
    { type: 'h2', text: 'Typical permitted development limits (England)' },
    {
      type: 'ul',
      items: [
        'The dormer must not extend beyond the existing roof plane facing a public highway, so front dormers normally need permission.',
        'The dormer must sit at least 20 cm back from the original eaves.',
        'Added roof volume is limited to 40 cubic metres for terraced houses and 50 cubic metres for detached and semi-detached houses.',
        'The dormer cannot be higher than the highest part of the existing roof.',
        'Materials should be similar in appearance to the existing house.',
        'Side-facing windows must be obscure glazed and non-opening unless more than 1.7 m above the floor.',
        'No balconies, verandas or raised platforms.',
      ],
    },
    { type: 'h2', text: 'When permission is always needed' },
    {
      type: 'p',
      text: 'Permitted development rights are removed or restricted in **conservation areas, national parks, areas of outstanding natural beauty, World Heritage Sites** and on **listed buildings**. Flats and maisonettes do not have these rights at all. Some houses also have rights removed by an earlier planning condition, often on newer estates. Check your deeds and your council planning register.',
    },
    { type: 'h2', text: 'Building permits and building regulations' },
    {
      type: 'p',
      text: 'Even when planning permission is not needed, the structure still has to be safe. That means a building permit in the US or **building regulations approval** in the UK. Inspectors check the structural design, fire escape from the new room, insulation, ventilation and stair access. Read our guide on [dormer building regulations and insulation](/blog/dormer-building-regulations-insulation) to see what is checked.',
    },
    { type: 'h2', text: 'Step-by-step application process' },
    {
      type: 'ul',
      items: [
        '**Check your property status.** Look for conservation area, listing or removed rights.',
        '**Sketch a design inside the limits.** Use the [dormer calculator](/calculator) to test sizes and pitch before paying for drawings.',
        '**Ask the authority.** A pre-application enquiry or a Lawful Development Certificate gives written confirmation.',
        '**Hire a designer or engineer** for structural drawings.',
        '**Submit the application** with drawings, fees and any neighbor notices.',
        '**Wait for a decision.** Typical determination time is 8 weeks for householder applications in England and varies in the US.',
      ],
    },
    { type: 'h2', text: 'Party walls and neighbors' },
    {
      type: 'p',
      text: 'If your dormer touches a shared wall or sits on a terrace, you may need to serve a **party wall notice**. Telling neighbors early also reduces objections during the formal consultation. A short conversation and a drawing often prevents months of delay.',
    },
    { type: 'h2', text: 'Common mistakes' },
    {
      type: 'ul',
      items: [
        'Starting work before confirming permissions, which can lead to enforcement action and forced removal.',
        'Measuring volume wrongly. Include all roof additions already made by previous owners.',
        'Ignoring covenants and homeowners association rules that sit outside planning law.',
        'Choosing materials that clash with the house, which is a frequent reason for refusal.',
      ],
    },
    {
      type: 'cta',
      title: 'Test your dormer size before you apply',
      text: 'Check floor area, roof surface and cost for different dimensions, so your drawings match your budget.',
    },
  ],
}
